/** @format */

import {useState} from 'react';

import Alert from '@mui/material/Alert';
import {Box, LinearProgress, Typography} from '@mui/material';
import {Trans} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {useSearchParams} from 'helpers/routerHelpers';
import {canCreateCompany} from 'helpers/restriction_helper';

import {useCompanyContext} from 'contexts/CompanyContext';
import {useMixpanelContext} from 'contexts/MixpanelContext';

import Form from './Form';
import {
  useCreateCompanyMutation,
  useGetCurrentUserQuery,
  useUpdateCompanyMutation,
} from './graphql/__generated__';

const Company = () => {
  const navigate = useNavigate();

  const searchParams = useSearchParams();
  const id = searchParams.get('id');

  const {mixpanel} = useMixpanelContext();
  const {switchCompany} = useCompanyContext();

  const {loading, error, data} = useGetCurrentUserQuery({
    variables: {id},
  });

  const [saveCompanyError, setSaveCompanyError] = useState('');

  const [createCompany, {loading: createCompanyLoading}] = useCreateCompanyMutation();
  const [updateCompany, {loading: updateCompanyLoading}] = useUpdateCompanyMutation();

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }
  if (loading || !data?.currentUser) {
    return <LinearProgress style={{flex: 1}} />;
  }

  const {company, subscription, restrictions, totalCompaniesCount, companies} = data.currentUser;

  const handleNext = async (name: string) => {
    try {
      if (company) {
        mixpanel.track('click_wizard_update_company_button');

        const {data: updateCompanyData} = await updateCompany({
          variables: {id: company.id, name: name},
        });

        switchCompany(updateCompanyData?.updateCompany);

        navigate({
          pathname: 'connect',
          search: `?companyId=${company.id}`,
        });
        return;
      }

      mixpanel.track('click_wizard_create_company_button');

      const {data: createCompanyData} = await createCompany({
        variables: {name: name},
      });

      switchCompany(createCompanyData?.createCompany);

      const companyId = createCompanyData?.createCompany.id;
      navigate(`/home/settings/wizard/company/connect?companyId=${companyId}`);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unknown error.';
      setSaveCompanyError(message);
    }
  };

  const canCreate = canCreateCompany({
    totalCompanies: totalCompaniesCount,
    aliveCompanies: companies.length,
    totalRestriction: restrictions.totalCompaniesCount,
    aliveRestriction: restrictions.aliveCompaniesCount,
  });

  return (
    <>
      <Typography variant='h1'>
        <Trans>Создание проекта</Trans>
      </Typography>
      <Box
        sx={{
          mt: 6,
        }}
      >
        <Form
          initialName={company?.name}
          canCreate={canCreate}
          loading={createCompanyLoading || updateCompanyLoading}
          isTrial={subscription.isTrial}
          isNew={!id}
          saveError={saveCompanyError}
          onSave={handleNext}
        />
      </Box>
    </>
  );
};

export default Company;
